import { useState } from 'react';

import { Plus } from 'lucide-react';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserList } from '@/components/users/UserList';
import { UserModal } from '@/components/users/UserModal';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser } from '@/hooks/useUsers';
import { extractValidationErrors, extractErrorMessage } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';

import type { UserFormValues, EditableUser } from '@/components/users/types';
import type { CreateUserDto, UpdateUserDto } from '@repo/shared-types';

export const UsersPage = () => {
  usePageTitle('Users');
  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = currentUser?.role === 'admin';

  const [page, setPage] = useState(1);
  const { data, isLoading } = useUsers({ page, limit: 10 });
  const users = data?.data ?? [];
  const pagination = data?.pagination;
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();

  const [editingUser, setEditingUser] = useState<EditableUser | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EditableUser | null>(null);
  const [modalErrors, setModalErrors] = useState<Array<{ field: string; message: string }>>([]);
  const [isSaving, setIsSaving] = useState(false);

  const { toast } = useToast();

  const canEditUser = (user: EditableUser) => isAdmin || user.id === currentUser?.id;
  const canDeleteUser = (user: EditableUser) => isAdmin && user.role !== 'admin';

  const handleToggleNotifications = async (user: EditableUser, enabled: boolean) => {
    try {
      await updateMutation.mutateAsync({
        id: user.id,
        data: { receive_notifications: enabled },
      });
      toast({
        variant: 'success',
        title: 'Settings updated',
        description: `Notifications for ${user.name} have been ${enabled ? 'enabled' : 'disabled'}.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to update settings',
        description: extractErrorMessage(error),
      });
    }
  };

  const handleNewUser = () => {
    if (!isAdmin) {
      toast({
        variant: 'destructive',
        title: 'Not allowed',
        description: 'Only admins can create users.',
      });
      return;
    }

    setEditingUser(null);
    setModalErrors([]);
    setIsModalOpen(true);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setModalErrors([]);
      setEditingUser(null);
    }
  };

  const handleEditUser = (user: EditableUser) => {
    if (!canEditUser(user)) {
      toast({
        variant: 'destructive',
        title: 'Not allowed',
        description: 'You can only change your own password.',
      });
      return;
    }

    setEditingUser(user);
    setModalErrors([]);
    setIsModalOpen(true);
  };

  const handleSubmitUser = async (values: UserFormValues) => {
    try {
      setModalErrors([]);
      setIsSaving(true);

      if (editingUser) {
        // Update existing
        const updateDto: UpdateUserDto = isAdmin
          ? {
              name: values.name,
              email: values.email,
              role: values.role,
              // Only 'client' role users carry configuration assignments
              configuration_ids: values.role === 'client' ? values.configuration_ids : [],
            }
          : {};

        if (!isAdmin && editingUser.id !== currentUser?.id) {
          toast({
            variant: 'destructive',
            title: 'Not allowed',
            description: 'You can only change your own password.',
          });
          return;
        }

        if (!isAdmin && values.password.trim().length === 0) {
          toast({
            variant: 'destructive',
            title: 'Password required',
            description: 'Enter a new password to update your account.',
          });
          return;
        }

        if (values.password.trim().length > 0) {
          updateDto.password = values.password;
        }

        await updateMutation.mutateAsync({
          id: editingUser.id,
          data: updateDto,
        });

        toast({
          variant: 'success',
          title: 'User updated',
          description: `"${values.name}" has been updated successfully.`,
        });
      } else {
        if (!isAdmin) {
          toast({
            variant: 'destructive',
            title: 'Not allowed',
            description: 'Only admins can create users.',
          });
          return;
        }

        // Create new
        const createDto: CreateUserDto = {
          name: values.name,
          email: values.email,
          role: values.role,
          password: values.password,
          ...(values.role === 'client' ? { configuration_ids: values.configuration_ids } : {}),
        };

        await createMutation.mutateAsync(createDto);

        toast({
          variant: 'success',
          title: 'User created',
          description: `"${values.name}" has been created successfully.`,
        });
      }

      setEditingUser(null);
      setIsModalOpen(false);
      setModalErrors([]);
    } catch (error) {
      const validationErrors = extractValidationErrors(error);
      if (validationErrors.length > 0) {
        setModalErrors(validationErrors);
      } else {
        toast({
          variant: 'destructive',
          title: editingUser ? 'Failed to update user' : 'Failed to create user',
          description: extractErrorMessage(error),
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRequest = (user: EditableUser) => {
    if (!canDeleteUser(user)) {
      toast({
        variant: 'destructive',
        title: 'Not allowed',
        description: 'Only admins can delete non-admin users.',
      });
      return;
    }

    setDeleteTarget(user);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    // Prevent deletion of admin users (additional frontend check)
    if (deleteTarget.role === 'admin') {
      toast({
        variant: 'destructive',
        title: 'Cannot delete admin',
        description: 'Admin users cannot be deleted.',
      });
      setDeleteTarget(null);
      return;
    }

    try {
      await deleteMutation.mutateAsync(deleteTarget.id);

      setDeleteTarget(null);

      toast({
        variant: 'success',
        title: 'User deleted',
        description: `"${deleteTarget.name}" has been deleted successfully.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete user',
        description: extractErrorMessage(error),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">User Access</p>
          <h1 className="text-3xl font-bold tracking-tight">Manage portal accounts</h1>
          <p className="text-muted-foreground max-w-2xl">
            {isAdmin
              ? 'Create and manage operator credentials, roles, and passwords.'
              : 'You can only change your own password.'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={handleNewUser}>
            <Plus className="h-4 w-4" />
            New User
          </Button>
        )}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
        </CardHeader>
        <CardContent>
          <UserList
            users={users}
            onEdit={handleEditUser}
            onDelete={handleDeleteRequest}
            onToggleNotifications={handleToggleNotifications}
            canEdit={canEditUser}
            canDelete={canDeleteUser}
          />
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total
                users)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <UserModal
        open={isModalOpen}
        onOpenChange={handleModalOpenChange}
        user={editingUser}
        onSubmit={handleSubmitUser}
        allowProfileEdit={isAdmin}
        errors={modalErrors}
        isLoading={isSaving}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        resourceName="user"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};
